// Admin: Commission Ledger — unified MLM + ATC + Marketplace commission audit trail
// Route: /admin/commission-ledger  ·  Backend: /api/admin/commission-ledger
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Download, RefreshCw, Filter } from 'lucide-react';
import AdminShell from '../../components/admin/AdminShell';
import api from '../../utils/api';

const DEAL_FILTERS = [
  { key: 'all',        label: 'All' },
  { key: 'b2c_retail', label: 'B2C' },
  { key: 'b2b_bulk',   label: 'B2B' },
  { key: 'marketplace',label: 'Marketplace' },
];

const RANGE_PRESETS = [
  { key: 'today', label: 'Today' },
  { key: '7d',    label: '7d' },
  { key: '30d',   label: '30d' },
  { key: 'all',   label: 'All' },
  { key: 'custom',label: 'Custom' },
];

function isoDaysAgo(days) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

export default function AdminCommissionLedger() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(false);
  const [dealFilter, setDealFilter] = useState('all');
  const [rangeKey, setRangeKey] = useState('30d');
  const [fromDate, setFromDate] = useState(isoDaysAgo(30));
  const [toDate, setToDate] = useState(new Date().toISOString().slice(0, 10));

  const applyRange = (key) => {
    setRangeKey(key);
    if (key === 'today') { setFromDate(isoDaysAgo(0)); setToDate(new Date().toISOString().slice(0, 10)); }
    else if (key === '7d') { setFromDate(isoDaysAgo(7)); setToDate(new Date().toISOString().slice(0, 10)); }
    else if (key === '30d') { setFromDate(isoDaysAgo(30)); setToDate(new Date().toISOString().slice(0, 10)); }
    else if (key === 'all') { setFromDate(''); setToDate(''); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { deal_type: dealFilter, limit: 500 };
      if (fromDate) params.from = fromDate;
      if (toDate) params.to = toDate;
      const r = await api.get('/admin/commission-ledger', { params });
      setRows(r.data.rows || []);
      setSummary(r.data.summary || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load ledger');
    } finally { setLoading(false); }
  }, [dealFilter, fromDate, toDate]);

  useEffect(() => { load(); }, [load]);

  const downloadCsv = () => {
    const p = new URLSearchParams();
    p.set('deal_type', dealFilter);
    if (fromDate) p.set('from', fromDate);
    if (toDate) p.set('to', toDate);
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/commission-ledger.csv?${p.toString()}`;
    // Use fetch with auth header, then blob download
    const token = localStorage.getItem('token');
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `commission_ledger_${Date.now()}.csv`;
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch(() => toast.error('CSV download failed'));
  };

  const totalDist = useMemo(
    () => (summary.total_l1 || 0) + (summary.total_l2 || 0) + (summary.total_l3 || 0),
    [summary]
  );

  return (
    <AdminShell>
      <div className="p-4 md:p-6 max-w-7xl mx-auto space-y-4" data-testid="admin-commission-ledger">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div>
            <h1 className="text-2xl md:text-3xl font-black">💰 Commission Ledger</h1>
            <p className="text-xs opacity-70">
              Unified MLM commission audit · ATC B2C/B2B + Marketplace orders · L1/L2/L3 splits + tax + admin net
            </p>
          </div>
          <div className="flex gap-2">
            <button onClick={load} disabled={loading} data-testid="ledger-refresh"
                    className="px-3 py-1.5 rounded-lg bg-slate-700 text-white text-xs font-black flex items-center gap-1">
              <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> Refresh
            </button>
            <button onClick={downloadCsv} data-testid="ledger-download-csv"
                    className="px-3 py-1.5 rounded-lg bg-emerald-500 text-slate-900 text-xs font-black flex items-center gap-1">
              <Download size={12} /> Export CSV
            </button>
          </div>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <SummaryCard label="Orders" value={summary.total_orders || 0} color="from-slate-800 to-slate-900" testid="ledger-sum-orders" />
          <SummaryCard label="Total Commission" value={`₹${(summary.total_commission || 0).toFixed(2)}`} color="from-emerald-600 to-emerald-800" testid="ledger-sum-commission" />
          <SummaryCard label="L1+L2+L3 Paid" value={`₹${totalDist.toFixed(2)}`} color="from-cyan-600 to-blue-800" testid="ledger-sum-mlm" />
          <SummaryCard label="Admin Net" value={`₹${(summary.total_admin_net || 0).toFixed(2)}`} color="from-fuchsia-600 to-purple-800" testid="ledger-sum-admin" />
        </div>

        {/* Filters */}
        <div className="rounded-2xl bg-slate-900/70 border border-slate-800 p-3 space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <Filter size={13} className="opacity-60" />
            <span className="text-[11px] opacity-70 font-black">DEAL TYPE:</span>
            {DEAL_FILTERS.map((f) => (
              <button key={f.key} onClick={() => setDealFilter(f.key)}
                      data-testid={`ledger-deal-${f.key}`}
                      className={`px-2.5 py-1 rounded-full text-[11px] font-black transition ${
                        dealFilter === f.key ? 'bg-white text-slate-900' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                      }`}>{f.label}</button>
            ))}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[11px] opacity-70 font-black">DATE RANGE:</span>
            {RANGE_PRESETS.map((r) => (
              <button key={r.key} onClick={() => applyRange(r.key)}
                      data-testid={`ledger-range-${r.key}`}
                      className={`px-2.5 py-1 rounded-full text-[11px] font-black transition ${
                        rangeKey === r.key ? 'bg-cyan-500 text-slate-900' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                      }`}>{r.label}</button>
            ))}
            {rangeKey === 'custom' && (
              <>
                <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
                       data-testid="ledger-from-date"
                       className="px-2 py-1 rounded-lg bg-slate-800 border border-slate-700 text-[11px]" />
                <span className="text-[11px] opacity-60">→</span>
                <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)}
                       data-testid="ledger-to-date"
                       className="px-2 py-1 rounded-lg bg-slate-800 border border-slate-700 text-[11px]" />
              </>
            )}
          </div>
        </div>

        {/* Table */}
        <div className="rounded-2xl bg-slate-900 border border-slate-800 overflow-x-auto">
          <table className="w-full text-[11px]">
            <thead className="bg-slate-950 sticky top-0">
              <tr className="text-left text-slate-400">
                <th className="p-2">Date/Time</th>
                <th className="p-2">Order ID</th>
                <th className="p-2">Deal Type</th>
                <th className="p-2 text-right">Total Amount</th>
                <th className="p-2 text-right">Commission %</th>
                <th className="p-2 text-right">Total Comm.</th>
                <th className="p-2 text-right">L1</th>
                <th className="p-2 text-right">L2</th>
                <th className="p-2 text-right">L3</th>
                <th className="p-2 text-right">Govt Tax</th>
                <th className="p-2 text-right">Admin Net</th>
                <th className="p-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && !loading && (
                <tr><td colSpan={12} className="p-8 text-center opacity-60">No commission events in this range. Once orders settle, they land here 💰</td></tr>
              )}
              {rows.map((r, i) => (
                <tr key={`${r.source}-${r.order_id}-${i}`} data-testid={`ledger-row-${(r.order_id || '').slice(0,8)}`}
                    className="border-t border-slate-800 hover:bg-slate-800/40">
                  <td className="p-2 text-slate-300 whitespace-nowrap">{r.created_at ? new Date(r.created_at).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' }) : '—'}</td>
                  <td className="p-2 font-mono text-cyan-300">{(r.order_id || '').slice(0, 10)}…</td>
                  <td className="p-2">
                    <span className={`px-1.5 py-0.5 rounded text-[9px] font-black ${
                      r.deal_type === 'B2C_RETAIL' ? 'bg-emerald-500/20 text-emerald-300' :
                      r.deal_type === 'B2B_BULK' ? 'bg-amber-500/20 text-amber-300' :
                      r.deal_type === 'MARKETPLACE' ? 'bg-fuchsia-500/20 text-fuchsia-300' :
                      'bg-slate-700/60 text-slate-300'
                    }`}>{r.deal_type}</span>
                  </td>
                  <td className="p-2 text-right text-slate-200">₹{Number(r.total_amount || 0).toFixed(2)}</td>
                  <td className="p-2 text-right text-yellow-300">{Number(r.commission_pct || 0).toFixed(2)}%</td>
                  <td className="p-2 text-right text-emerald-300 font-black">₹{Number(r.total_commission || 0).toFixed(2)}</td>
                  <td className="p-2 text-right">
                    <div className="text-cyan-300 font-black">₹{Number(r.l1_amount || 0).toFixed(2)}</div>
                    {r.l1_recipient && <div className="text-[9px] opacity-60">@{r.l1_recipient}</div>}
                  </td>
                  <td className="p-2 text-right">
                    <div className="text-cyan-300 font-black">₹{Number(r.l2_amount || 0).toFixed(2)}</div>
                    {r.l2_recipient && <div className="text-[9px] opacity-60">@{r.l2_recipient}</div>}
                  </td>
                  <td className="p-2 text-right">
                    <div className="text-cyan-300 font-black">₹{Number(r.l3_amount || 0).toFixed(2)}</div>
                    {r.l3_recipient && <div className="text-[9px] opacity-60">@{r.l3_recipient}</div>}
                  </td>
                  <td className="p-2 text-right text-rose-300">₹{Number(r.govt_tax || 0).toFixed(2)}</td>
                  <td className="p-2 text-right text-fuchsia-300 font-black">₹{Number(r.admin_net || 0).toFixed(2)}</td>
                  <td className="p-2">
                    <span className={`px-1.5 py-0.5 rounded text-[9px] font-black ${
                      r.status === 'COMPLETED' ? 'bg-emerald-500/20 text-emerald-300' :
                      r.status === 'REFUNDED' ? 'bg-rose-500/20 text-rose-300' :
                      'bg-slate-700/60 text-slate-300'
                    }`}>{r.status || '—'}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="text-[10px] opacity-60 text-center">
          Rows shown: {rows.length} · Data source: <code className="opacity-80">atc_commission_split_logs</code> + <code className="opacity-80">avx_transactions[marketplace_*]</code>
        </div>
      </div>
    </AdminShell>
  );
}

function SummaryCard({ label, value, color, testid }) {
  return (
    <div data-testid={testid} className={`rounded-2xl bg-gradient-to-br ${color} border border-white/10 p-3`}>
      <div className="text-[10px] opacity-70 font-black uppercase tracking-wide">{label}</div>
      <div className="text-xl font-black mt-1">{value}</div>
    </div>
  );
}
